import clsx from "clsx";
import { ChevronDown, Paperclip, Settings2, X } from "lucide-react";
import {
  useState,
  useRef,
  useEffect,
  type FormEvent,
  type ChangeEvent,
  type ClipboardEvent,
  type DragEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent,
} from "react";

import { useDictation } from "@/hooks/useDictation";
import { useModels } from "@/hooks/useModels";
import { useCreateJob } from "@/hooks/useSession";
import { uploadAttachments } from "@/lib/api";
import { formatAttachmentSize, mergeAttachmentFiles } from "@/lib/attachments";
import { useStore } from "@/lib/store";
import type { ModelsResponse } from "@/lib/types";

import { DictationButton } from "./DictationButton";
import { DictationStatus } from "./DictationStatus";
import { SendButton } from "./SendButton";
import { getModelLabel } from "./stats/formatters";

const TEXTAREA_ID = "sherlog-message";

interface MessageFormProps {
  autoFocus?: boolean;
}

export function isModelSelectorDisabled({
  isBusy,
  isModelsLoading,
  modelsData,
}: {
  isBusy: boolean;
  isModelsLoading: boolean;
  modelsData: ModelsResponse | undefined;
}): boolean {
  return isBusy || isModelsLoading || !modelsData;
}

function autoResizeTextarea(textarea: HTMLTextAreaElement): void {
  textarea.style.height = "auto";
  textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
}

export function MessageForm({ autoFocus }: MessageFormProps): React.JSX.Element {
  const [message, setMessage] = useState("");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);
  const modelId = useStore((state) => state.composerModelId);
  const setModelId = useStore((state) => state.setComposerModelId);
  const showToast = useStore((state) => state.showToast);
  const { data: modelsData, isLoading: isModelsLoading } = useModels();
  const createJob = useCreateJob();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const modelMenuRef = useRef<HTMLDivElement>(null);
  const dictation = useDictation({
    onTranscript: (text) => {
      insertTranscriptAtCursor(text);
    },
    onError: (errorMessage) => {
      showToast(errorMessage, "error");
    },
  });

  const isRecording = dictation.status === "recording";
  const isProcessingAudio =
    dictation.status === "transcribing" || dictation.status === "processing";
  const isSubmitting = createJob.isPending || isUploading;
  const isBusy = isSubmitting || isProcessingAudio;
  const selectedModelId = modelId ?? modelsData?.defaultModelId ?? "";
  const [modelMenuOpen, setModelMenuOpen] = useState(false);

  useEffect(() => {
    if (!autoFocus) return;
    if (isBusy) return;
    // Defer to avoid focusing before layout / route transitions finish.
    const id = window.setTimeout(() => {
      textareaRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(id);
  }, [autoFocus, isBusy]);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    autoResizeTextarea(textarea);
  }, [message]);

  useEffect(() => {
    if (modelId || !modelsData?.defaultModelId) {
      return;
    }

    setModelId(modelsData.defaultModelId);
  }, [modelId, modelsData?.defaultModelId, setModelId]);

  useEffect(() => {
    if (!modelMenuOpen) return;

    function handlePointerDown(event: MouseEvent): void {
      if (!modelMenuRef.current?.contains(event.target as Node)) {
        setModelMenuOpen(false);
      }
    }

    function handleKeyDown(event: KeyboardEvent): void {
      if (event.key === "Escape") {
        setModelMenuOpen(false);
      }
    }

    window.addEventListener("mousedown", handlePointerDown);
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("mousedown", handlePointerDown);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [modelMenuOpen]);

  function handleModelChange(nextModelId: string): void {
    const normalized = nextModelId.trim() || null;
    if ((modelId ?? null) === normalized) {
      return;
    }

    setModelId(normalized);
    setModelMenuOpen(false);
  }

  const selectedModel = modelsData?.models.find((model) => model.fullId === selectedModelId);
  const selectedModelLabel = selectedModel
    ? `${selectedModel.providerName} · ${getModelLabel(
        selectedModel.providerId,
        selectedModel.modelId,
        selectedModel.modelName,
      )}`
    : "OpenCode default";

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!message.trim() || isBusy || isRecording) return;

    try {
      let attachmentIds: string[] | undefined;
      if (selectedFiles.length > 0) {
        setIsUploading(true);
        const uploadResponse = await uploadAttachments(selectedFiles);
        attachmentIds = uploadResponse.attachments.map((attachment) => attachment.id);
      }

      await createJob.mutateAsync({
        message: message.trim(),
        modelId: selectedModelId || undefined,
        attachmentIds,
      });
      setMessage("");
      setSelectedFiles([]);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to send message";
      showToast(errorMessage, "error");
    } finally {
      setIsUploading(false);
    }
  }

  function handleKeyDown(e: ReactKeyboardEvent<HTMLTextAreaElement>): void {
    if (e.key !== "Enter") {
      return;
    }

    if (e.ctrlKey) {
      e.preventDefault();
      const textarea = e.currentTarget;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const nextMessage = `${message.slice(0, start)}\n${message.slice(end)}`;
      setMessage(nextMessage);
      requestAnimationFrame(() => {
        textarea.selectionStart = start + 1;
        textarea.selectionEnd = start + 1;
        autoResizeTextarea(textarea);
      });
      return;
    }

    if (e.shiftKey) {
      return;
    }

    e.preventDefault();
    if (message.trim() && !isBusy && !isRecording) {
      void handleSubmit(e as unknown as FormEvent);
    }
  }

  function insertTranscriptAtCursor(transcript: string): void {
    const normalizedTranscript = transcript.trim();
    if (!normalizedTranscript) {
      return;
    }

    const textarea = textareaRef.current;
    if (!textarea) {
      setMessage((current) =>
        current.trim().length > 0 ? `${current} ${normalizedTranscript}` : normalizedTranscript,
      );
      return;
    }

    const selectionStart = textarea.selectionStart ?? textarea.value.length;
    const selectionEnd = textarea.selectionEnd ?? textarea.value.length;

    setMessage((current) => {
      const start = Math.min(selectionStart, current.length);
      const end = Math.min(selectionEnd, current.length);
      const before = current.slice(0, start);
      const after = current.slice(end);
      const prefix = before.length > 0 && !/\s$/.test(before) ? " " : "";
      const suffix = after.length > 0 && !/^\s/.test(after) ? " " : "";
      const insertion = `${prefix}${normalizedTranscript}${suffix}`;
      const nextMessage = `${before}${insertion}${after}`;
      const nextCursor = before.length + insertion.length;

      requestAnimationFrame(() => {
        const target = textareaRef.current;
        if (!target) return;
        target.focus();
        target.selectionStart = nextCursor;
        target.selectionEnd = nextCursor;
        autoResizeTextarea(target);
      });

      return nextMessage;
    });
  }

  function addFiles(incomingFiles: File[]): void {
    const { files, error } = mergeAttachmentFiles(selectedFiles, incomingFiles);
    if (error) {
      showToast(error, "error");
      return;
    }
    setSelectedFiles(files);
  }

  function handleFileChange(e: ChangeEvent<HTMLInputElement>): void {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      addFiles(files);
    }
    // Reset so selecting the same file again triggers change.
    e.target.value = "";
  }

  function handlePaste(e: ClipboardEvent<HTMLTextAreaElement>): void {
    const files = Array.from(e.clipboardData.files || []);
    if (files.length === 0) {
      return;
    }

    e.preventDefault();
    addFiles(files);
  }

  function handleDragOver(e: DragEvent<HTMLDivElement>): void {
    e.preventDefault();
    setIsDraggingFiles(true);
  }

  function handleDragLeave(e: DragEvent<HTMLDivElement>): void {
    e.preventDefault();
    if (e.currentTarget.contains(e.relatedTarget as Node | null)) {
      return;
    }
    setIsDraggingFiles(false);
  }

  function handleDrop(e: DragEvent<HTMLDivElement>): void {
    e.preventDefault();
    setIsDraggingFiles(false);
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length > 0) {
      addFiles(files);
    }
  }

  function removeSelectedFile(index: number): void {
    setSelectedFiles((current) => current.filter((_, currentIndex) => currentIndex !== index));
  }

  function focusTextarea(): void {
    textareaRef.current?.focus();
  }

  function handleContainerPointerDown(e: PointerEvent<HTMLDivElement>): void {
    if (e.button !== 0) return;
    const target = e.target as HTMLElement;
    if (target.closest("textarea,button,a,input,select")) return;
    e.preventDefault();
    focusTextarea();
  }

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <div className="relative" ref={modelMenuRef}>
          <button
            type="button"
            onClick={() => setModelMenuOpen((open) => !open)}
            className="text-cyber-muted hover:text-cyber-text inline-flex items-center gap-1 font-mono text-[12px] leading-[15.5px] transition-colors"
            title="Model for this new chat"
            aria-label="Model for this new chat"
            aria-expanded={modelMenuOpen}
            disabled={isModelSelectorDisabled({ isBusy, isModelsLoading, modelsData })}
          >
            <Settings2 size={12} className="shrink-0" />
            <span className="truncate">
              {isModelsLoading ? "Loading models..." : selectedModelLabel}
            </span>
            <ChevronDown
              size={12}
              className={clsx("shrink-0 transition-transform", modelMenuOpen && "rotate-180")}
            />
          </button>

          <div
            className={clsx(
              "border-cyber-border bg-cyber-bg/95 absolute top-[calc(100%+8px)] right-0 z-20 max-h-72 w-[320px] overflow-y-auto rounded-xl border p-2 shadow-[0_16px_32px_rgba(0,0,0,0.28)] backdrop-blur transition-all",
              modelMenuOpen
                ? "translate-y-0 opacity-100"
                : "pointer-events-none -translate-y-1 opacity-0",
            )}
          >
            {!selectedModelId && (
              <button
                type="button"
                onClick={() => handleModelChange("")}
                className="text-cyber-text hover:bg-cyber-surface/50 flex w-full items-center rounded-lg px-3 py-2 text-left text-xs transition-colors"
              >
                OpenCode default
              </button>
            )}
            {modelsData?.models.map((model) => {
              const label = `${model.providerName} · ${getModelLabel(model.providerId, model.modelId, model.modelName)}`;
              return (
                <button
                  key={model.fullId}
                  type="button"
                  onClick={() => handleModelChange(model.fullId)}
                  className={clsx(
                    "flex w-full items-center rounded-lg px-3 py-2 text-left text-xs transition-colors",
                    selectedModelId === model.fullId
                      ? "bg-cyber-surface/60 text-cyber-text"
                      : "text-cyber-muted hover:bg-cyber-surface/50 hover:text-cyber-text",
                  )}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Input */}
      <form onSubmit={handleSubmit}>
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          multiple
          onChange={handleFileChange}
          disabled={isBusy}
        />

        <div
          className={clsx(
            "focus-ring shadow-soft border-cyber-border bg-cyber-surface min-h-[60px] cursor-text rounded-2xl border px-4 py-3",
            isDraggingFiles && "border-cyber-cyan/60 bg-cyber-cyan/5",
          )}
          onPointerDown={handleContainerPointerDown}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className="flex items-center gap-2">
            <span className="text-cyber-cyan text-lg">{">"}</span>
            <textarea
              id={TEXTAREA_ID}
              ref={textareaRef}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onInput={(e) => autoResizeTextarea(e.currentTarget)}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              placeholder="Ask Sherlog something..."
              required
              rows={1}
              className="text-cyber-text placeholder-cyber-muted/50 flex-1 cursor-text resize-none overflow-y-auto rounded-none border-none bg-transparent text-base focus:shadow-none focus:ring-0 focus:outline-none sm:text-sm"
              autoComplete="off"
              disabled={isBusy}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isBusy || isRecording}
              className="text-cyber-muted hover:text-cyber-cyan inline-flex items-center justify-center p-1 transition-all"
              title="Attach files"
              aria-label="Attach files"
            >
              <Paperclip size={16} />
            </button>
            <DictationButton
              variant="cyan"
              status={dictation.status}
              isSupported={dictation.isSupported}
              disabled={isBusy}
              onClick={() => {
                void dictation.toggleRecording();
              }}
              onPointerDown={(e) => {
                e.preventDefault();
                focusTextarea();
              }}
            />
            <SendButton
              variant="cyan"
              disabled={isBusy || isRecording || !message.trim()}
              pending={isSubmitting}
              onPointerDown={(e) => {
                // Keep focus in the textarea when clicking send.
                e.preventDefault();
                focusTextarea();
              }}
            />
          </div>

          <DictationStatus
            variant="cyan"
            status={dictation.status}
            elapsedMs={dictation.elapsedMs}
            level={dictation.level}
          />

          {!dictation.isSupported && (
            <div className="text-cyber-muted mt-2 text-[11px]">
              Voice dictation is unavailable in this browser.
            </div>
          )}

          {(selectedFiles.length > 0 || isDraggingFiles) && (
            <div className="border-cyber-border/60 mt-2 border-t pt-2">
              {selectedFiles.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {selectedFiles.map((file, index) => (
                    <div
                      key={`${file.name}:${file.size}:${file.lastModified}`}
                      className="border-cyber-border bg-cyber-bg/60 text-cyber-text inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs"
                    >
                      <span className="max-w-[180px] truncate" title={file.name}>
                        {file.name}
                      </span>
                      <span className="text-cyber-muted">{formatAttachmentSize(file.size)}</span>
                      <button
                        type="button"
                        onClick={() => removeSelectedFile(index)}
                        className="text-cyber-muted hover:text-cyber-red transition-all"
                        aria-label={`Remove ${file.name}`}
                        title="Remove"
                        disabled={isBusy}
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-cyber-muted text-xs">Drop files to attach</div>
              )}
            </div>
          )}
        </div>
      </form>
    </div>
  );
}
